import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { z } from "zod";
import type { ZodSchema } from "zod";

/**
 * Various query parameters which different Serpentine API requests expect
 */
export const SrpRequestParamsSchema = z
	.object({
		/**
		 * The id of the requested resource (part, API ID, etc.)
		 */
		id: z.number(),
		/**
		 * The id of the audience (group, user, etc.) to which the resource belongs
		 */
		audience: z.number(),
		/**
		 * The id of the fund to which the resource belongs
		 */
		fund_id: z.number().or(z.number().array()),
		/**
		 * The id of the share class to which the resource belongs
		 */
		share_class_id: z.number().or(z.number().array()),
		/**
		 * Some APIs use shareclass_id
		 */
		shareclass_id: z.number().or(z.number().array()),
		/**
		 * Documents APIs use share_classes
		 */
		share_classes: z.number().or(z.number().array()),
		/**
		 * Advanced datatable configuration + structure
		 */
		share_class: z.number().or(z.number().array()),
		/**
		 * The id of the resource
		 */
		resource_id: z.number().or(z.number().array()),
		/**
		 * The language id of the resource
		 */
		language: z.number(),
		/**
		 * The fund uuid
		 */
		fund_uuid: z.string(),
		/**
		 * A flag used for internal testing, might trigger slightly differnet behaviour like not sending emails
		 */
		internal: z.enum(["True", "False"]),
		/**
		 * Some APIs use funds instead of fund_id
		 */
		funds: z.number().or(z.number().array()),
		/**
		 * Literature type ids, used for documents APIs
		 */
		lit_types: z.array(z.string().or(z.number())),
		/**
		 * The as at date of the requested resource
		 */
		as_at_date: z.string(),
		/**
		 * The version of the requested resource
		 */
		version: z.enum(["live", "preview", "draft"]),
		/**
		 * Action is an additional optional parameter used in the fund centre API
		 */
		action: z.string(),
		/**
		 * prices start date, required by prices parts
		 */
		price_part_start_date: z.string(),
		/**
		 * prices end date, required by prices parts
		 */
		price_part_end_date: z.string(),
		/**
		 * price type id, required by prices date metadata api
		 */
		type_id: z.number().or(z.number().array()),
		/**
		 * granularity of the available dates returned by the prices date metadata api
		 */
		granularity: z.enum(["days"]),
	})
	.partial();

export type SrpRequestParams = z.infer<typeof SrpRequestParamsSchema>;

export type GETArgs = {
	pathname: string;
	params?: SrpRequestParams;
	baseURL?: string;
};

export type POSTArgs = {
	pathname: string;
	requestInit?: RequestInit;
	body?: RequestInit["body"];
	csrftoken?: string;
	params?: SrpRequestParams;
	baseURL?: string;
};

export type SrpApiContext = {
	baseURL: string;
	getFn: (args: GETArgs) => Promise<unknown>;
	postFn: (args: POSTArgs) => Promise<unknown>;
};

export type ServiceName =
	| "part"
	| "literature"
	| "datatable"
	| "userdata"
	| "fund-centre";

export type ServiceProviderProps = PropsWithChildren<{
	url: string;
	baseParams?: Partial<SrpRequestParams>;
}>;

export const LitStructureSchema = z.object({
	set_id: z.number(),
	display_name: z.string(),
	settings: z.object({}),
	rank: z.number(),
	document_count: z.number(),
	lit_types: z.array(z.number()),
	show_history: z.boolean(),
	document_naming_rule: z.string(),
	show_document_date: z.boolean(),
	document_date_source: z.string().nullable(),
	document_date_format: z.string().nullable(),
	show_document_note: z.boolean(),
	show_document_icon: z.boolean(),
	show_empty_tab: z.boolean(),
	show_language_label: z.boolean(),
});
export type LitStructure = z.infer<typeof LitStructureSchema>;

export const LitStructureResponseSchema = z.object({
	data: z.array(LitStructureSchema),
	settings: z.object({}),
});
export type LitStructureResponse = z.infer<typeof LitStructureResponseSchema>;

export const DocSchema = z.object({
	id: z.number(),
	lit_type_id: z.number(),
	date: z.string(),
	display_name: z.string(),
	category: z.string(),
	secondary_category: z.string(),
	description: z.string().nullable(),
	language_aware_display_name: z.string(),
	entity_display_names: z.record(z.string()),
	level: z.string(),
	language_id: z.number(),
	language: z.string(),
	rank: z.number(),
	url: z.string(),
});
export type Doc = z.infer<typeof DocSchema>;

export const DocResponseSchema = z.object({
	data: z.array(DocSchema),
});
export type DocResponse = z.infer<typeof DocResponseSchema>;

export const CoBrandingPDFURLSchema = z.object({
	url: z.string(),
	output_name: z.string(),
	logo_field: z.string(),
});
export type CoBrandingPDFURL = z.infer<typeof CoBrandingPDFURLSchema>;
export const CoBrandingServicePayloadSchema = z.object({
	service: z.enum(["co-branding"]).default("co-branding"),
	tenant: z.enum(["Premier Miton"]),
	zip: z.boolean().default(true),
	pdf_urls: CoBrandingPDFURLSchema.array(),
	logo_image: z.string(),
});
export type CoBrandingServicePayload = z.infer<
	typeof CoBrandingServicePayloadSchema
>;
